import { castArray, get } from 'lodash';
import type { CustomControlCode } from '@common/enums';
import type { ProcessActivityContextData } from '@common/formEngine/processActivityContext.types';
import type { DeepPick, DeepPickPath } from '@common/utils/deepPickUtils';

export type DependsOn<
    Data extends Record<string, unknown>,
    Result extends boolean | string[],
    TCustomControlCode extends CustomControlCode | undefined = undefined,
    Version extends number = 1,
    Key extends DeepPickPath<ProcessActivityContextData<Data, TCustomControlCode, Version>> = DeepPickPath<
        ProcessActivityContextData<Data, TCustomControlCode, Version>
    >,
> = {
    fieldKeys: Key[];
    fn: (data: DeepPick<ProcessActivityContextData<Data, TCustomControlCode, Version>, Key>) => Result;
};

export function dependsOn<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined = undefined,
    Version extends number = 1,
    Key extends DeepPickPath<ProcessActivityContextData<Data, TCustomControlCode, Version>> = DeepPickPath<
        ProcessActivityContextData<Data, TCustomControlCode, Version>
    >,
>(
    fieldKey: Key | Key[],
    fn?: (data: DeepPick<ProcessActivityContextData<Data, TCustomControlCode, Version>, Key>) => boolean
): DependsOn<Data, boolean, TCustomControlCode, Version, Key> {
    const fieldKeys = castArray(fieldKey);
    const defaultFn = (data: DeepPick<ProcessActivityContextData<Data, TCustomControlCode, Version>, Key>) =>
        fieldKeys.every((key) => Boolean(get(data, key)));

    return {
        fieldKeys,
        fn: fn ?? defaultFn,
    };
}

export function valueDependsOn<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined = undefined,
    Version extends number = 1,
    Key extends DeepPickPath<ProcessActivityContextData<Data, TCustomControlCode, Version>> = DeepPickPath<
        ProcessActivityContextData<Data, TCustomControlCode, Version>
    >,
>(
    fieldKey: Key | Key[],
    fn: (data: DeepPick<ProcessActivityContextData<Data, TCustomControlCode, Version>, Key>) => string[]
): DependsOn<Data, string[], TCustomControlCode, Version, Key> {
    return {
        fieldKeys: castArray(fieldKey),
        fn,
    };
}

export function isDependsOn<
    Data extends Record<string, unknown>,
    Res extends boolean | string[],
    TCustomControlCode extends CustomControlCode | undefined = undefined,
    Version extends number = 1,
>(value: unknown): value is DependsOn<Data, Res, TCustomControlCode, Version> {
    return (
        typeof value === 'object' &&
        value !== null &&
        'fieldKeys' in value &&
        (typeof value.fieldKeys === 'string' || Array.isArray(value.fieldKeys)) &&
        'fn' in value &&
        typeof value.fn === 'function'
    );
}
